/**
 * Pure helpers behind the workspace's "Other Statistics" section — keyed
 * monthly values and drivers on any non-room-night stat account. Kept out of
 * the component so the cell resolution and paste parsing are unit-testable.
 *
 * Effective plan value for a month = keyed ?? driven. A keyed cell is a month
 * override on top of the driver (the money rows' rule).
 */

import { MONTHS } from '../types';
import type { StatDriverConfig, StatProjectedContext, StatValueAccount, StatValueSeries } from '../api';
import type { ForecastAccount } from '../types';

export type StatCellSource = 'keyed' | 'driven' | 'actual' | null;

export interface StatCell {
  /** What the collapsed month cell shows (null → blank). */
  display: number | null;
  source: StatCellSource;
  /** The keyed month override (null when nothing keyed). */
  keyed: number | null;
  /** What the account's driver produced for the month (null when no driver / no basis). */
  driven: number | null;
  /** keyed ?? driven — the plan value. */
  plan: number | null;
  /** One-line explanation of the driven value. */
  note: string;
  actual: number | null;
  budget: number | null;
  lyActual: number | null;
  lyBudget: number | null;
}

export interface StatRow {
  account: StatValueAccount & { driver?: StatDriverConfig | null };
  cells: StatCell[];
  /** Σ of what's displayed across the year (plan + rolled-in actuals). */
  total: number;
  hasKeyed: boolean;
  hasDriver: boolean;
}

const seriesValue = (series: StatValueSeries | undefined, accountId: number, monthNumber: number): number | null => {
  const v = series?.[accountId]?.[monthNumber] ?? series?.[String(accountId)]?.[String(monthNumber)];
  return v === undefined || v === null ? null : Number(v);
};

export interface BuildStatRowsInput {
  accounts: (StatValueAccount & { driver?: StatDriverConfig | null })[];
  values: StatValueSeries;
  driven?: StatValueSeries;
  notes?: Record<string, Record<string, string>>;
  actuals: StatValueSeries;
  lyActuals: StatValueSeries;
  budget: StatValueSeries;
  lyBudget: StatValueSeries;
  /** Month keys ('jan'…) whose column shows recorded actuals on screen. */
  actualMonthKeys: Set<string>;
}

/**
 * One row per account. On an actual month the cell shows the GL actual (a
 * closed month with no posting still shows the plan so a stat feed that
 * starts mid-year doesn't blank the row); otherwise keyed ?? driven.
 */
export function buildStatRows(input: BuildStatRowsInput): StatRow[] {
  const { accounts, values, driven, notes, actuals, lyActuals, budget, lyBudget, actualMonthKeys } = input;
  return accounts.map((account) => {
    const id = account.chartOfAccountId;
    let total = 0;
    let hasKeyed = false;
    const hasDriver = !!account.driver && account.driver.driverType !== 'manual';
    const cells = MONTHS.map((monthKey, idx) => {
      const m = idx + 1;
      const keyed = seriesValue(values, id, m);
      const drivenV = hasDriver ? seriesValue(driven, id, m) : null;
      const plan = keyed !== null ? keyed : drivenV;
      const actual = seriesValue(actuals, id, m);
      if (keyed !== null) hasKeyed = true;
      const useActual = actualMonthKeys.has(monthKey) && actual !== null;
      const display = useActual ? actual : plan;
      const source: StatCellSource = useActual ? 'actual' : keyed !== null ? 'keyed' : drivenV !== null ? 'driven' : null;
      if (display !== null) total += display;
      const note = notes?.[id]?.[m] ?? notes?.[String(id)]?.[String(m)] ?? '';
      return {
        display,
        source,
        keyed,
        driven: drivenV,
        plan,
        note,
        actual,
        budget: seriesValue(budget, id, m),
        lyActual: seriesValue(lyActuals, id, m),
        lyBudget: seriesValue(lyBudget, id, m),
      };
    });
    return { account, cells, total, hasKeyed, hasDriver };
  });
}

/**
 * Value for an expanded-month comparison column. Mirrors the money rows'
 * column keys; anything money-specific ($POR, % Rev) has no meaning on a
 * count and returns null (rendered as a dash).
 */
export function statColumnValue(cell: StatCell, columnKey: string): number | null {
  const plan = cell.plan;
  const actual = cell.actual;
  const bud = cell.budget;
  const ly = cell.lyActual;
  const lyBud = cell.lyBudget;
  const diff = (a: number | null, b: number | null) => (a === null || b === null ? null : a - b);
  switch (columnKey) {
    case 'forecast': return plan;
    case 'actual': return actual;
    case 'budget': return bud;
    case 'ly': return ly;
    case 'lyBudget': return lyBud;
    case 'var': return diff(actual, plan);
    case 'vsBud': return diff(actual, bud);
    case 'budVsFcst': return diff(plan, bud);
    case 'vsLy': return diff(actual, ly);
    case 'budgetVsLyBudget': return diff(plan, lyBud);
    default: return null;
  }
}

/** Year total for an expanded Total column: Σ of the column across months. */
export function statColumnTotal(cells: StatCell[], columnKey: string): number | null {
  let sum = 0;
  let any = false;
  for (const cell of cells) {
    const v = statColumnValue(cell, columnKey);
    if (v === null) continue;
    any = true;
    sum += v;
  }
  return any ? sum : null;
}

/** Typed input → number | null (blank clears). Rejects negatives and NaN. */
export function parseStatInput(raw: string): { value: number | null } | { error: string } {
  const text = raw.replace(/[,\s]/g, '');
  if (text === '') return { value: null };
  const n = Number(text);
  if (!Number.isFinite(n)) return { error: 'Enter a number' };
  if (n < 0) return { error: 'Statistics cannot be negative' };
  return { value: Math.round(n * 100) / 100 };
}

/**
 * Clipboard text → one value per pasted cell, left to right (tabs, commas or
 * newlines separate cells; a row of a spreadsheet pastes as tab-separated).
 * Blank cells become null (clear); unparsable cells are skipped by returning
 * undefined so the caller can leave that month alone.
 */
export function parsePastedValues(text: string): (number | null | undefined)[] {
  const flat = text.replace(/\r\n?/g, '\n').split(/\n|\t/);
  // A single line with commas as separators (no tabs / newlines) is a list,
  // but "1,200" on its own is a thousands separator.
  const cells = flat.length === 1 && /,/.test(flat[0]) && !/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(flat[0].trim())
    ? flat[0].split(',')
    : flat;
  // Trailing newline from a spreadsheet copy is not an extra blank cell.
  while (cells.length > 1 && cells[cells.length - 1].trim() === '') cells.pop();
  return cells.map((c) => {
    const parsed = parseStatInput(c);
    return 'error' in parsed ? undefined : parsed.value;
  });
}

export const formatStatNumber = (v: number | null): string => {
  if (v === null || !Number.isFinite(v)) return '';
  const rounded = Math.round(v * 100) / 100;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString()
    : rounded.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

/** Short chip text for a driver config, e.g. "LY Actual +5%", "Per ORN · LY rate". */
export function describeStatDriver(driver: StatDriverConfig | null | undefined, label: string | undefined): string {
  if (!driver || driver.driverType === 'manual') return 'Manual';
  const name = label || driver.driverType;
  const input = driver.driverInput;
  const pct = (p: number) => `${p >= 0 ? '+' : ''}${p}%`;
  switch (driver.driverType) {
    case 'annual_spread':
      return input === null ? name : `${name} · ${formatStatNumber(input)}`;
    case 'per_orn':
    case 'per_arn':
      if (driver.rateSource === 'manual') return `${name} · ${input === null ? '?' : formatStatNumber(input)}`;
      return `${name} · ${driver.rateSource === 'ttm' ? 'TTM' : 'LY'} rate${input ? ` ${pct(input)}` : ''}`;
    case 'per_revenue':
      if (driver.rateSource === 'manual') return `${name} · $${input === null ? '?' : formatStatNumber(input)}`;
      return `${name} · LY $/unit${input ? ` ${pct(input)}` : ''}`;
    default:
      return input ? `${name} ${pct(input)}` : name;
  }
}

/**
 * The on-screen room-night and revenue projections the stat drivers multiply
 * against, in the shape getStatValues / getStatAccountExport accept. Null when
 * the grid has neither yet.
 */
export function buildStatProjected(
  roomStats: { occupiedRooms: Record<string, number>; availableRooms: Record<string, number> } | null | undefined,
  revenueAccounts: ForecastAccount[] | undefined,
): StatProjectedContext | null {
  const accounts = revenueAccounts || [];
  if (!roomStats && accounts.length === 0) return null;
  const revenue: Record<string, Record<string, number>> = {};
  accounts.forEach((a) => {
    const months: Record<string, number> = {};
    MONTHS.forEach((m) => { const v = a.monthlyValues?.[m]; if (typeof v === 'number') months[m] = v; });
    revenue[a.chartOfAccountId] = months;
  });
  return { occupiedRooms: roomStats?.occupiedRooms || {}, availableRooms: roomStats?.availableRooms || {}, revenue };
}
